/**
 * Sign In Screen
 * 
 * Native sign-in flow with responsive design.
 * Desktop: Centered card layout
 * Mobile: Full-screen with fixed footer
 * 
 * @module components/auth/SignInScreen
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@sequelae/lore-core';
import { validate as emailValidator } from 'email-validator';
import { useAuth } from '@/contexts/AuthContext';
import { getSignInPasswordError, isValidSignInPassword } from '@/utils/passwordValidation';
import './SignInScreen.css';

export const SignInScreen: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [signInError, setSignInError] = useState('');
  
  // Local submitting state for immediate UI feedback
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Combined loading state
  const isBusy = isLoading || isSubmitting;

  /**
   * Validate email format
   */
  const validateEmail = useCallback((value: string): string => {
    if (!value) {
      return 'Email is required.';
    }
    if (!emailValidator(value)) {
      return 'Please enter a valid email address.';
    }
    return '';
  }, []);

  /**
   * Handle email input change
   */
  const handleEmailChange = (value: string) => {
    setEmail(value);
    const error = validateEmail(value);
    setEmailError(error);
    if (signInError) setSignInError('');
  };

  /**
   * Handle password input change
   */
  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const error = getSignInPasswordError(value);
    setPasswordError(error);
    if (signInError) setSignInError('');
  };

  /**
   * Check if form is valid for submission
   */
  const isFormValid = (): boolean => {
    return (
      email.length > 0 &&
      emailValidator(email) &&
      password.length > 0 &&
      isValidSignInPassword(password)
    );
  };

  /**
   * Map Firebase error codes to user-friendly messages
   */
  const getErrorMessage = (error: any): string => {
    // Extract error code from various error formats (Firebase sometimes sends code in message only)
    const errorMessage = error?.message || '';
    let errorCode = error?.code || error?.errorCode || '';
    if (!errorCode && typeof errorMessage === 'string' && errorMessage.includes('auth/')) {
      const match = errorMessage.match(/auth\/([a-z-]+)/);
      if (match) errorCode = `auth/${match[1]}`;
    }
    
    console.log('[SignIn] Error details:', { code: errorCode, message: errorMessage, error });
    
    // Map Firebase Auth error codes to user-friendly messages
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address. Please check your email or create an account.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again or use "Forgot your password?" to reset it.';
      case 'auth/invalid-credential':
        return 'Incorrect email or password. Please check your credentials and try again, or use "Forgot your password?" to reset it.';
      case 'auth/invalid-email':
        return 'The email address format is not valid. Please enter a valid email.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support at hello@lore.co for assistance.';
      case 'auth/too-many-requests':
        return 'Too many failed sign-in attempts. Please wait a few minutes before trying again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      case 'auth/internal-error':
        return 'An internal error occurred. Please try again later.';
      case 'auth/invalid-api-key':
        return 'Configuration error. Please contact support.';
      case 'auth/operation-not-allowed':
        return 'Sign-in is temporarily unavailable. Please try again later.';
      default:
        // If we have a message, use it; otherwise provide generic message
        if (errorMessage && errorMessage !== 'Firebase: Error (auth/unknown).') {
          // Clean up Firebase error message format
          const cleanMessage = errorMessage
            .replace(/^Firebase: /, '')
            .replace(/\(auth\/[^)]+\)\.?$/, '')
            .trim();
          return cleanMessage || 'Sign in failed. Please check your email and password.';
        }
        return 'Sign in failed. Please check your email and password and try again.';
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      console.warn('[SignIn] Form validation failed');
      // Show inline validation errors
      if (!email) {
        setEmailError('Email is required.');
      }
      if (!password) {
        setPasswordError('Password is required.');
      }
      return;
    }

    setSignInError('');
    setIsSubmitting(true);

    try {
      console.log('[SignIn] Attempting sign in for:', email);
      await login(email, password);
      console.log('[SignIn] Sign in successful');
    } catch (error: any) {
      console.error('[SignIn] Sign in failed:', error);
      const message = getErrorMessage(error);
      setSignInError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle forgot password click
   */
  const handleForgotPassword = () => {
    console.log('[SignIn] Navigating to forgot password screen');
    navigate('/forgot-password');
  };

  /**
   * Handle close button
   */
  const handleClose = () => {
    navigate('/auth');
  };

  return (
    <div className="signin-screen">
      <div className="signin-card">
        {/* Header with title and close button */}
        <header className="signin-header">
          <h1 className="signin-title">Sign in to your Lore account</h1>
          <button 
            className="close-btn" 
            onClick={handleClose}
            aria-label="Close"
            disabled={isBusy}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Form */}
        <form onSubmit={handleSubmit} className="signin-form">
          {/* Email Field */}
          <div className="field-group">
            <label htmlFor="signin-email" className="field-label">
              Email Address
            </label>
            <div className="input-container">
              <input
                id="signin-email"
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="email@example.com"
                disabled={isBusy}
                className={`field-input ${emailError ? 'has-error' : ''}`}
                autoComplete="email"
                autoFocus
              />
            </div>
            {emailError && <p className="field-error">{emailError}</p>}
          </div>

          {/* Password Field */}
          <div className="field-group">
            <label htmlFor="signin-password" className="field-label">
              Password
            </label>
            <div className="input-container">
              <input
                id="signin-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder="Enter your password"
                disabled={isBusy}
                className={`field-input ${passwordError ? 'has-error' : ''}`}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="visibility-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isBusy}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {passwordError && <p className="field-error">{passwordError}</p>}
          </div>

          {/* Forgot Password Link */}
          <button
            type="button"
            className="forgot-link"
            onClick={handleForgotPassword}
            disabled={isBusy}
          >
            Forgot your password?
          </button>

          {/* Sign In Error */}
          {signInError && (
            <div className="error-banner" role="alert" aria-live="polite">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <p>{signInError}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={!isFormValid() || isBusy}
          >
            {isBusy ? <Spinner /> : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};
